import { assertNotEmpty } from '@festina-life/core'
import { useState } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import {
  isSubTemplatedField,
  isSubTemplatedFieldValue,
  isTemplateField,
  type SubTemplateValueSetupFieldValue,
  ValueSetupRole,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import { isNotInherited } from '../../uiModels/templateOperations/types'
import type { TemplateFieldTableParent } from './TemplateFieldsTable'

export const useEditSubtemplates = (field: TemplateFieldTableParent) => {
  const [isAddVariantModalOpen, setIsAddVariantModalOpen] = useState<boolean>()
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const handleAddParent = () => {
    setIsAddVariantModalOpen(true)
  }
  const handleRemoveParent = async () => {
    if (
      isTemplateField(field) &&
      field.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE
    ) {
      assertNotEmpty(field.value)

      await field.update(null)
    } else if (isSubTemplatedFieldValue(field)) {
      const variantParent = field.subTemplatedField
      assertNotEmpty(variantParent)

      const nextVariants = [...(variantParent.value?.variants ?? [])]
      const currentVariantPosition = nextVariants.findIndex(
        (v) => v.subTemplateIdentifier === field.subTemplateIdentifier,
      )
      nextVariants.splice(currentVariantPosition, 1)

      const currentValue =
        variantParent.value ??
        (generateEmptyFieldValue({
          field: variantParent,
          meta: metaModel,
        }) as SubTemplateValueSetupFieldValue)

      ;(
        variantParent.update as (
          nextValue: SubTemplateValueSetupFieldValue,
        ) => void
      )({
        ...currentValue,
        variants: nextVariants,
      })
    } else if (isTemplateField(field) && isSubTemplatedField(field)) {
      await field.update(null)
    } else {
      throw new Error('Unsupported delete operation removing parent')
    }
  }

  const canAddSubtemplate =
    isDraftTemplate &&
    isTemplateField(field) &&
    isSubTemplatedField(field) &&
    !isSubTemplatedFieldValue(field) &&
    field.role !== ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE &&
    (field.role === ValueSetupRole.IDENTIFIABLE_SUB_TEMPLATE ||
      !field.value ||
      (field.value.variants?.length ?? 0) <
        (field.value.availableVariantTypes?.length ?? 0))

  const canRemoveSubTemplate =
    isDraftTemplate &&
    isNotInherited(field) &&
    (isSubTemplatedFieldValue(field) ||
      (isTemplateField(field) &&
        isSubTemplatedField(field) &&
        ((field.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE &&
          !field.isRenderingEmpty) ||
          (field.role !== ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE &&
            field.value))))

  return {
    canAddSubtemplate,
    canRemoveSubTemplate,
    onRemoveParent: handleRemoveParent,
    onAddParent: handleAddParent,
    isAddVariantModalOpen,
    onCloseVariantModal: () => setIsAddVariantModalOpen(false),
  }
}
